import { describeError, type Permission } from '@kdk/shared';
import { Alert, Button } from '@mui/material';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ForbiddenPage } from '../app/ForbiddenPage';
import { LoadingScreen } from '../app/LoadingScreen';
import { useAuth } from './AuthProvider';
import { buildLoginPath } from './loginPath';

/** 未ログインならログイン画面へ(戻り先つき)。確認中は読み込み表示、確認に失敗したら再試行できるエラー表示。 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const { pathname, search, hash } = useLocation();
  switch (auth.status) {
    case 'loading':
      return <LoadingScreen />;
    case 'error':
      return (
        <Alert
          severity="error"
          sx={{ m: 3 }}
          action={
            <>
              <Button color="inherit" size="small" onClick={auth.retry}>
                再試行
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void auth.logout();
                }}
              >
                ログイン画面へ
              </Button>
            </>
          }
        >
          ログイン状態を確認できませんでした。{auth.error === null ? '' : describeError(auth.error)}
        </Alert>
      );
    case 'anonymous':
      return (
        <Navigate
          replace
          to={
            auth.reason === 'signed-out' ? '/login' : buildLoginPath(`${pathname}${search}${hash}`)
          }
          state={auth.reason === 'expired' ? { reason: 'expired' } : null}
        />
      );
    case 'authenticated':
      return <>{children}</>;
  }
};

/** 権限がなければ「権限なし」ページ(サイドバーは残る)。RequireAuth の内側で使う。 */
export const RequirePermission = ({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) => {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <ForbiddenPage />;
};
